import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "aura-composer relative flex h-full w-full min-w-0 bg-[#F5F6F8]" }
const _hoisted_2 = { class: "aura-composer-main min-h-0 min-w-0 flex-1 p-2 sm:p-4" }
const _hoisted_3 = {
  key: 0,
  class: "flex h-full items-center justify-center rounded-2xl border border-[#F5F5F5] bg-white shadow-[0px_0px_0px_1px_rgba(23,23,23,0.05),0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] sm:rounded-3xl"
}
const _hoisted_4 = { class: "flex flex-col items-center gap-4 text-center" }
const _hoisted_5 = { class: "text-sm font-medium text-[#626468]" }
const _hoisted_6 = {
  key: 1,
  class: "flex h-full items-center justify-center bg-[#F8FAFC] p-4 sm:p-6"
}
const _hoisted_7 = { class: "w-full max-w-xl rounded-2xl border border-[#E5E7EB] bg-white p-5 text-center shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] sm:rounded-3xl sm:p-8" }
const _hoisted_8 = { class: "text-2xl font-bold text-[#242B59]" }
const _hoisted_9 = { class: "mx-auto mt-3 max-w-md text-sm leading-6 text-[#626468]" }
const _hoisted_10 = { class: "mt-6 rounded-2xl bg-[#F9FAFB] px-4 py-4 text-sm text-[#6B7280]" }
const _hoisted_11 = {
  key: 2,
  class: "flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-[#F5F5F5] bg-white shadow-[0px_0px_0px_1px_rgba(23,23,23,0.05),0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] sm:rounded-3xl"
}
const _hoisted_12 = {
  key: 0,
  class: "px-4 pt-4 sm:px-6"
}
const _hoisted_13 = {
  key: 1,
  class: "mt-3"
}
const _hoisted_14 = { class: "min-h-0 min-w-0 flex-1 overflow-hidden" }
const _hoisted_15 = { class: "fixed inset-0 z-10 overflow-y-auto" }
const _hoisted_16 = { class: "flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0" }
const _hoisted_17 = { class: "bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4" }
const _hoisted_18 = { class: "sm:flex sm:items-start" }
const _hoisted_19 = { class: "mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left" }
const _hoisted_20 = { class: "mt-2" }
const _hoisted_21 = { class: "text-sm text-gray-500" }
const _hoisted_22 = { class: "bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6" }

import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';

import AuraChatLayout from '@/views/pages/aura/AuraChatLayout.vue';
import AuraSidebar from '@/views/pages/aura/components/AuraSidebar.vue';
import AuraHeader from '@/views/pages/aura/components/AuraHeader.vue';
import AuraValidationMessage from '@/views/pages/aura/components/AuraValidationMessage.vue';
import TemporalWarning from '@/views/pages/aura/components/TemporalWarning.vue';
import { useAuraStore } from '@/stores/modules/aura';
import { AuraService } from '@/services';
import { useAuraToast } from '@/composables/useAuraToast';
import { useAuraResponsive } from '@/composables/useAuraResponsive';
import { can } from '@/composables/useAuth';
import { Permission } from '@/types';
import type { AuraConversation, AuraContext, AuraState } from '@/types';

const MAX_MESSAGE_LENGTH = 2000;

// Local UI state only

export default /*@__PURE__*/_defineComponent({
  __name: 'AuraComposer',
  setup(__props) {

/**
 * AURA Composer - Layout container orchestrating sidebar, header, and chat
 * SRP: Only responsible for layout composition and event delegation
 */
const { t } = useI18n();
const store = useAuraStore();
const { info: showInfoToast } = useAuraToast();
const { isMobile } = useAuraResponsive();
const isSending = ref(false);
const isHistoryLoading = ref(false);
const isContextLoading = ref(true);
const auraContext = ref<AuraContext | null>(null);
const chatLayoutRef = ref<InstanceType<typeof AuraChatLayout> | null>(null);
const isInternalLoading = computed(
    () => store.isLoading || isSending.value || isHistoryLoading.value || isContextLoading.value,
);
const deleteDialogOpen = ref(false);
const conversationToDelete = ref<AuraConversation | null>(null);
const sidebarOpen = ref(false);

watch(
    isMobile,
    (mobile) => {
        sidebarOpen.value = !mobile;
    },
    { immediate: true },
);

const openSidebar = () => {
    sidebarOpen.value = true;
};

const closeSidebar = () => {
    if (isMobile.value) sidebarOpen.value = false;
};

// Permissions
const canViewHistory = computed(() => can(Permission.VIEW_AURA_CONVERSATION));
const canUseAura = computed(() => can(Permission.VIEW_AURA));
const canDeleteConversation = computed(() => can(Permission.DELETE_AURA_CONVERSATION));
const isAuraAvailable = computed(() => canUseAura.value && auraContext.value?.enabled === true);

// Computed from store for message display
const messages = computed(() => store.messages);
const currentConversationId = computed(() => store.currentConversation?.id || null);
const suggestedQuestions = computed(() =>
    (auraContext.value?.suggestedQuestions ?? []).map((item) => item.questionText),
);
const validationError = computed(() => store.validationError);
const temporalConflict = computed(() => store.temporalConflict);
const loadingMessage = computed(() => {
    if (store.validationStep === 'idle') {
        return t('aura.chat.loading');
    }
    return t(`aura.chat.loadingSteps.${store.validationStep}`);
});

// Load history on mount (only if has permission)
onMounted(async () => {
    isContextLoading.value = true;
    try {
        const response = await AuraService.getContext();
        auraContext.value = response.data;
    } catch (error) {
        console.error('Failed to load AURA context:', error);
        auraContext.value = null;
    } finally {
        isContextLoading.value = false;
    }

    if (!canViewHistory.value) return;
    isHistoryLoading.value = true;
    try {
        await store.loadHistory();
    } catch (error) {
        console.error('Failed to load history:', error);
    } finally {
        isHistoryLoading.value = false;
    }
});

// Event handlers - delegate to store
const handleSelectConversation = async (conversation: AuraConversation) => {
    try {
        await store.loadConversation(conversation.id);
    } catch (error) {
        console.error('Failed to load conversation:', error);
    }
};

const handleCreateConversation = async () => {
    try {
        await store.createConversation();
    } catch (error) {
        console.error('Failed to create conversation:', error);
    }
};

const handleRetryFailedQuestion = async (payload: {
    userMsgIndex: number;
    auraMsgIndex: number;
    question: string;
}) => {
    store.clearValidationError();
    store.clearTemporalConflict();
    store.setValidationStep('idle');

    const conversationId = currentConversationId.value;
    if (!conversationId) return;

    const userMsg = store.messages[payload.userMsgIndex];
    const auraMsg = store.messages[payload.auraMsgIndex];

    const idsToDelete: number[] = [];
    if (userMsg && typeof userMsg.id === 'number') {
        idsToDelete.push(userMsg.id);
    }
    if (auraMsg && typeof auraMsg.id === 'number') {
        idsToDelete.push(auraMsg.id);
    }

    try {
        await store.deleteConversationMessages(conversationId, idsToDelete);
    } catch (error) {
        console.error('Failed to delete failed messages from history:', error);
    }
};

const handleSendMessage = async (content: string) => {
    if (content.trim().length > MAX_MESSAGE_LENGTH) {
        store.setValidationError({
            type: 'MESSAGE_TOO_LONG',
            message: `Sua mensagem excede o limite de ${MAX_MESSAGE_LENGTH} caracteres.`,
            suggestion: 'Resuma a pergunta ou envie em partes menores.',
        });
        showInfoToast(`Limite de ${MAX_MESSAGE_LENGTH} caracteres excedido.`);
        return;
    }

    // Create conversation if not exists, passing the message as initialQuestion
    if (!currentConversationId.value) {
        await store.createConversation(undefined, content);
    }

    store.clearValidationError();
    store.clearTemporalConflict();

    const filtersBeforeSend = store.activeFilters
        ? {
              ...store.activeFilters,
          }
        : null;

    // Simulate validation progress steps sequentially
    let currentStepIndex = 0;
    const steps: Array<AuraState['validationStep']> = [
        'validating_scope',
        'classifying_intent',
        'checking_temporal',
        'processing',
    ];
    store.setValidationStep(steps[currentStepIndex]);

    let stepInterval: ReturnType<typeof setInterval> | null = null;
    stepInterval = setInterval(() => {
        if (currentStepIndex < steps.length - 1) {
            currentStepIndex++;
            store.setValidationStep(steps[currentStepIndex]);
        } else if (stepInterval) {
            clearInterval(stepInterval);
        }
    }, 5000);

    isSending.value = true;
    try {
        const response = await store.sendMessage(content);
        if (store.consumeAuxiliaryFilterLoadFailure()) {
            showInfoToast(t('aura.chat.auxiliaryFiltersUnavailable'));
        }
        const effectiveDateWindow = response.metadata?.filterResolution?.effectiveDateWindow;
        const dateAdjusted =
            response.metadata?.filterResolution?.source === 'question' &&
            !!effectiveDateWindow &&
            (filtersBeforeSend?.dateFrom !== effectiveDateWindow.dateFrom ||
                filtersBeforeSend?.dateTo !== effectiveDateWindow.dateTo);

        const responseClassification = response.response.classification;
        const metadataClassification = response.metadata?.classification;
        const effectiveClassification = responseClassification || metadataClassification;
        const apiConflict = effectiveClassification?.time_conflict;

        if (effectiveClassification?.time_coherent === false && apiConflict) {
            store.clearValidationError();
            store.setTemporalConflict({
                questionPeriod:
                    response.metadata?.filterResolution?.questionPeriod?.original ||
                    apiConflict.questionPeriod ||
                    effectiveClassification.entities?.period ||
                    '',
                filterPeriod: response.metadata?.filterResolution?.effectiveDateWindow
                    ? `${response.metadata.filterResolution.effectiveDateWindow.dateFrom || ''} - ${response.metadata.filterResolution.effectiveDateWindow.dateTo || ''}`
                    : apiConflict.filterPeriod || '',
                details: apiConflict.details || undefined,
                suggestedDateFrom:
                    response.metadata?.filterResolution?.questionDateWindow?.dateFrom || undefined,
                suggestedDateTo:
                    response.metadata?.filterResolution?.questionDateWindow?.dateTo || undefined,
            });
        }

        // --- filter_conflict: agent / queue disambiguation ---
        const filterConflict =
            response.response?.status?.filter_conflict ||
            effectiveClassification?.filter_conflict ||
            null;
        const operatorBeforeSend = filtersBeforeSend?.operatorId || null;
        const operatorAfterSend = store.activeFilters?.operatorId || null;
        const selectedAgent = store.availableAgents.find((agent) => agent.id === operatorAfterSend);
        let filterAdjustmentToastMessage = '';

        if (filterConflict) {
            if (filterConflict.status === 'resolved') {
                const label = filterConflict.type === 'agent' ? 'Agente' : 'Fila';
                filterAdjustmentToastMessage = `${label} "${filterConflict.resolvedName}" selecionado automaticamente.`;
            } else if (filterConflict.status === 'not_found') {
                store.setValidationError({
                    type: 'FILTER_NOT_FOUND',
                    message: `Não encontrei nenhum ${filterConflict.type === 'agent' ? 'agente' : 'fila'} com o nome "${filterConflict.extractedValue}".`,
                    suggestion: '',
                });
            }
        }

        if (!filterAdjustmentToastMessage && operatorBeforeSend !== operatorAfterSend) {
            filterAdjustmentToastMessage = selectedAgent
                ? `Agente "${selectedAgent.name}" selecionado automaticamente.`
                : 'Filtro de agente removido automaticamente.';
        }

        if (filterAdjustmentToastMessage) {
            showInfoToast(filterAdjustmentToastMessage);
        }

        if (
            response.response.status?.status === 'blocked' &&
            response.response.status.code !== 'TIME_CONFLICT'
        ) {
            store.setValidationError({
                type: response.response.status.code || 'INVALID_INTENT',
                message:
                    response.response.status.message ||
                    response.response.summary?.content ||
                    t('aura.validation.genericMessage'),
                suggestion:
                    (response.response.status.details?.suggestion as string | undefined) || '',
            });
        }

        if (dateAdjusted) {
            showInfoToast(t('aura.chat.dateAdjustedByAura'));
        }
    } catch (error) {
        console.error('Failed to get AURA response:', error);
        // Add error message to chat as styled message
        if (store.error) {
            store.addErrorMessage(store.error);
            store.clearError();
        }
    } finally {
        if (stepInterval) {
            clearInterval(stepInterval);
        }
        if (!store.temporalConflict) {
            store.setValidationStep('idle');
        }
        isSending.value = false;
    }
};

const handleSendWithFilter = async (payload: {
    message: string;
    filterKey: string;
    filterId: number;
}) => {
    const overrideFilters = {
        ...(store.activeFilters || {}),
        [payload.filterKey]: payload.filterId,
    };
    store.setFilters(overrideFilters);
    await handleSendMessage(payload.message);
};

const handleAutoCorrect = () => {
    const suggestedDateFrom = store.temporalConflict?.suggestedDateFrom;
    const suggestedDateTo = store.temporalConflict?.suggestedDateTo;

    if (!suggestedDateFrom || !suggestedDateTo) {
        return;
    }

    store.setFilters({
        ...(store.activeFilters || {}),
        dateFrom: suggestedDateFrom,
        dateTo: suggestedDateTo,
    });
    store.clearTemporalConflict();
    store.setValidationStep('idle');
    showInfoToast(t('aura.chat.temporalConflictAutoCorrected'));
};

const handleManualEdit = () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
    showInfoToast(t('aura.chat.temporalConflictManualEdit'));
};

const handleProceed = () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
};

const handleRephrase = async () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
    await nextTick();
    chatLayoutRef.value?.focusComposer();
};

const handleDismissTemporalWarning = () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
};

const handleDismissValidationError = () => {
    store.clearValidationError();
};

const handleDeleteConversation = async (conversation: AuraConversation) => {
    if (!canDeleteConversation.value) return;

    conversationToDelete.value = conversation;
    deleteDialogOpen.value = true;
};

const confirmDeleteConversation = async () => {
    if (!conversationToDelete.value) return;

    try {
        await store.deleteConversation(conversationToDelete.value.id);
    } catch (error) {
        console.error('Failed to delete conversation:', error);
    } finally {
        deleteDialogOpen.value = false;
        conversationToDelete.value = null;
    }
};

const cancelDeleteConversation = () => {
    deleteDialogOpen.value = false;
    conversationToDelete.value = null;
};

return (_ctx: any,_cache: any) => {
  const _component_UiSpinner = _resolveComponent("UiSpinner")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isAuraAvailable.value && canViewHistory.value && _unref(isMobile) && sidebarOpen.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "fixed inset-0 z-40 cursor-default bg-[#242B59]/35 backdrop-blur-[1px]",
          "aria-label": "Fechar histórico de conversas",
          onClick: closeSidebar
        }))
      : _createCommentVNode("", true),
    (isAuraAvailable.value && canViewHistory.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass([
                'z-50 h-full flex-shrink-0 transition-transform duration-200 ease-out',
                _unref(isMobile)
                    ? [
                          'fixed inset-y-0 left-0 shadow-2xl',
                          sidebarOpen.value ? 'translate-x-0' : '-translate-x-full',
                      ]
                    : 'relative translate-x-0',
            ])
        }, [
          _createVNode(AuraSidebar, {
            "is-history-loading": isHistoryLoading.value,
            onSelect: _cache[0] || (_cache[0] = ($event: any) => {
                    handleSelectConversation($event);
                    closeSidebar();
                }),
            onCreate: _cache[1] || (_cache[1] = ($event: any) => {
                    handleCreateConversation();
                    closeSidebar();
                }),
            onDelete: handleDeleteConversation
          }, null, 8, ["is-history-loading"])
        ], 2))
      : _createCommentVNode("", true),
    _createElementVNode("main", _hoisted_2, [
      (isContextLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_UiSpinner, { class: "h-10 w-10 text-[#242B59]" }),
              _createElementVNode("p", _hoisted_5, _toDisplayString(loadingMessage.value), 1)
            ])
          ]))
        : (!isAuraAvailable.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _cache[2] || (_cache[2] = _createElementVNode("div", { class: "mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EEF2FF]" }, [
                  _createElementVNode("div", { class: "flex h-10 w-10 items-center justify-center rounded-full bg-[#242B59] text-xl text-white" }, " 🔒 ")
                ], -1)),
                _cache[3] || (_cache[3] = _createElementVNode("div", { class: "mx-auto mb-3 inline-flex items-center rounded-full bg-[#E6F9F5] px-3 py-1.5" }, [
                  _createElementVNode("span", { class: "text-xs font-semibold uppercase tracking-[0.16em] text-[#242B59]" }, " mobcall AURA™ ")
                ], -1)),
                _createElementVNode("h2", _hoisted_8, _toDisplayString(_unref(t)('aura.accessDenied.title')), 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)('aura.accessDenied.message')), 1),
                _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)('aura.chat.disclaimer')), 1)
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createVNode(AuraHeader, {
                "vertical-name": auraContext.value?.linkedVertical?.verticalName,
                "show-menu": canViewHistory.value,
                compact: _unref(isMobile),
                onMenu: openSidebar
              }, null, 8, ["vertical-name", "show-menu", "compact"]),
              (validationError.value || temporalConflict.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    (validationError.value)
                      ? (_openBlock(), _createBlock(AuraValidationMessage, {
                          key: 0,
                          "error-type": validationError.value.type as any,
                          message: validationError.value.message,
                          suggestion: validationError.value.suggestion,
                          dismissible: "",
                          onDismiss: handleDismissValidationError
                        }, null, 8, ["error-type", "message", "suggestion"]))
                      : _createCommentVNode("", true),
                    (temporalConflict.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                          _createVNode(TemporalWarning, {
                            conflict: temporalConflict.value,
                            onAutoCorrect: handleAutoCorrect,
                            onManualEdit: handleManualEdit,
                            onProceed: handleProceed,
                            onRephrase: handleRephrase,
                            onDismiss: handleDismissTemporalWarning
                          }, null, 8, ["conflict"])
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_14, [
                _createVNode(AuraChatLayout, {
                  ref_key: "chatLayoutRef",
                  ref: chatLayoutRef,
                  messages: messages.value,
                  "is-loading": isInternalLoading.value,
                  "empty-prompts": suggestedQuestions.value,
                  "context-title": auraContext.value?.linkedVertical?.verticalName ?? '',
                  "loading-message": loadingMessage.value,
                  onSend: handleSendMessage,
                  onRetryFailedQuestion: handleRetryFailedQuestion,
                  onSendWithFilter: handleSendWithFilter
                }, null, 8, ["messages", "is-loading", "empty-prompts", "context-title", "loading-message"])
              ])
            ]))
    ]),
    _createVNode(_unref(TransitionRoot), {
      as: "template",
      show: deleteDialogOpen.value
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Dialog), {
          as: "div",
          class: "relative z-50",
          onClose: cancelDeleteConversation
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(TransitionChild), {
              as: "template",
              enter: "ease-out duration-300",
              "enter-from": "opacity-0",
              "enter-to": "opacity-100",
              leave: "ease-in duration-200",
              "leave-from": "opacity-100",
              "leave-to": "opacity-0"
            }, {
              default: _withCtx(() => _cache[4] || (_cache[4] = [
                _createElementVNode("div", { class: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" }, null, -1)
              ])),
              _: 1,
              __: [4]
            }),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("div", _hoisted_16, [
                _createVNode(_unref(TransitionChild), {
                  as: "template",
                  enter: "ease-out duration-300",
                  "enter-from": "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
                  "enter-to": "opacity-100 translate-y-0 sm:scale-100",
                  leave: "ease-in duration-200",
                  "leave-from": "opacity-100 translate-y-0 sm:scale-100",
                  "leave-to": "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DialogPanel), { class: "relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg" }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_17, [
                          _createElementVNode("div", _hoisted_18, [
                            _cache[5] || (_cache[5] = _createElementVNode("div", { class: "mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" }, [
                              _createElementVNode("svg", {
                                class: "h-6 w-6 text-red-600",
                                fill: "none",
                                viewBox: "0 0 24 24",
                                "stroke-width": "1.5",
                                stroke: "currentColor",
                                "aria-hidden": "true"
                              }, [
                                _createElementVNode("path", {
                                  "stroke-linecap": "round",
                                  "stroke-linejoin": "round",
                                  d: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                })
                              ])
                            ], -1)),
                            _createElementVNode("div", _hoisted_19, [
                              _createVNode(_unref(DialogTitle), {
                                as: "h3",
                                class: "text-base font-semibold leading-6 text-gray-900"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)('aura.conversation.deleteTitle')), 1)
                                ]),
                                _: 1
                              }),
                              _createElementVNode("div", _hoisted_20, [
                                _createElementVNode("p", _hoisted_21, _toDisplayString(_unref(t)('aura.conversation.deleteMessage', {
                                                            title:
                                                                conversationToDelete.value?.title ||
                                                                'Untitled',
                                                        })), 1)
                              ])
                            ])
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_22, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto",
                            onClick: confirmDeleteConversation
                          }, _toDisplayString(_unref(t)('aura.conversation.confirm')), 1),
                          _createElementVNode("button", {
                            type: "button",
                            class: "mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto",
                            onClick: cancelDeleteConversation
                          }, _toDisplayString(_unref(t)('aura.conversation.cancel')), 1)
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ])
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["show"])
  ]))
}
}

})